// RING REF — the CPU REFERENCE of the device ring painter (ringgen.js, the
// gensched pass 'ring'; gs-ring-genome D7): one far-ring sector at one level
// = (px >> level) per side RGBA8 sRGB of albedo x E / W, painted from the
// bake's three per-sector inputs (meta.panorama.classes: pipeline/bake.py
// "ring semantics" + bake_ring_semantic) and the device's OWN height raster
// of the ground tree's outer nodes under the sector (H1: the far field's mesh;
// ring.js relief) — no photo pixel, no shipped normal.
// Plain JS (importable by Node like groundref.js), PER TEXEL, so ringgen's
// GLSL follows it line by line; the light model and its quantisation are
// groundref.js's own (imported, never re-typed). The gate is
// scripts/check-ring.mjs (R1-R5, R8, R9 --gpu: window.__vr.ringCheck).
//
// API
//   ringWorld(meta, genome, pal, cj, gtTable) -> world (once per world):
//       meta = the bake meta (panorama.classes of RING_CLASSES_FORMAT, else
//       THROWS; tex.light, lighting, tex.s2.anchor ?? ANCHOR_DEFAULT, which
//       must equal classes.residual.anchor, else THROWS); genome = the
//       surface/genome.json doc; pal = painterpal.json; cj =
//       pipeline/classes.json (a null-paint class's palette key);
//       gtTable = pipeline/genome_noise.json (undefined: genometex's own)
//   ringRecord(world, key) -> genomeTexParams of one genome record (cached in
//       world.gP; the heavy solve — ringgen slices it, one record a run)
//   ringInputs(t, genomeUrl) -> the pass's input urls: the tile's cls, lit,
//       res + the world's genome (level-independent: one fetch per sector)
//   ringExact(ab, rec, n, what) -> Promise<Uint8Array n>: an EXACT raster
//       (cls: raw u8 w h; res: raw u8 RGB rw rh — meta.panorama.classes
//       sectors[id].cls / .res) taken AS FETCHED: never an image decode,
//       never a canvas read-back (canvas farbling flips low bits: 254 water
//       -> 255); its byte count n and its sha16 (rec.sha, SHA-256 of the
//       bytes) checked once. A mismatch THROWS a ringFault (permanent: the
//       same bytes fail the same way — gensched remembers it, no retry)
//   ringFault(msg) -> an Error marked permanent (gensched's deterministic
//       failure: dead for the page, the pass's fail() draws its fallback)
//   ringFallbackRGB(meta, pal, cj) -> sRGB-8 [r, g, b]: a sector's colour
//       when its texture cannot be generated — the ring's class histogram
//       (classes.stats.hist) over painterpal's base colours (water: deep),
//       linear mean x the flat light (E / W of an upward face in full sun:
//       the painter's light model at n_y 1, vis 1, sky 1)
//   gmChunks(relief) -> [{x, y, z: Float64Array, idx, lvl}] the relief nodes
//       (ring.js relief(id): [{k, tx, ty, bytes}], GM03) decoded by
//       groundm/codec.js (decodeNode, meshOf) and placed in game metres
//       (node origin tx x nodeSide(k), ty x nodeSide(k)); lvl = k. y is the
//       ground's flat-earth truth (no curvature: curve.js is the client's one
//       curvature transform)
//   decodeChunks(ab, chunks, pano) -> [{x, y, z: Float64Array, idx}] one RNG1
//       file's chunks in game metres (the painter fixture's meshes,
//       scripts/fixtures/ring: their y carries the old ring's earth drop)
//   rasterHeights(chunks, rect, w, h) -> Float32Array w h (row 0 north): the
//       chunks' triangles top-down, barycentric at the texel centres
//       rect.x0 + (i + 0.5) (x1 - x0) / w; the FINEST level covering a texel
//       wins (a chunk's lvl, lower = finer; none = 0 — ringgen draws the
//       levels coarse -> fine, the depth cleared between them), within one
//       level the HIGHEST covering triangle (the GPU's depth test: a skirt
//       sliver under a surface never shows through; on a shared edge both
//       give the same height); uncovered texels: the nearest covered of the
//       row (left first), then of the column (groundref stage 0's fill)
//   quickHeights(at, world, rect, w, h) -> Float32Array w h (row 0 north):
//       the QUICK paint's heights from the ring core's curved sampler at(x, z)
//       + ringDrop (flat-earth truth, as the relief raster)
//   ringPaint(world, sector, level, heights, opts) -> {rgba (row 0 = the
//       SOUTH edge: the GL texture ringgen writes, v 0 = z1), albedo
//       Float32Array 3 (sRGB 0..255), lin Float32Array 3 (the linear albedo
//       before the sRGB clip), E Uint8Array 3 (E / W sRGB-8), lit Uint8Array
//       3, h (true altitude), w, h_px, tx, tz} — albedo / lin / E / lit row 0
//       = NORTH. sector = {id, tier, rect, px, cls: Uint8Array w h, lit:
//       Uint8Array w 2h (grey: sun rows over sky rows), res: {codes
//       Uint8Array rw rh 3, px, rect}} (decoded, row 0 north); heights =
//       rasterHeights at this level (the relief's flat-earth y: the true
//       altitude as it stands).
//   opts: { noResidual (R = 1), noVis (sun = sky visibility 1; sector.lit
//       unread), seed (a per-sector noise offset, m) — the gate's red twins;
//       noTex (T = 1: the record's class mean alone, no genomeTexParams solve)
//       — with noVis the QUICK paint (ringgen.js: the sector's first colour) }
//
// THE PAINTER (per texel at game (x, z); level l: w = px >> l, texel tx =
// cell px / w — the texture covers the class raster's rect exactly, = the
// class cell at level 0):
//   h      the raster = the true altitude (the GROUND-M nodes are the flat-
//          earth truth: NO earth drop re-added — the old ring meshes were
//          curved from the frame and needed ringDrop back; ringDrop stays
//          for that model's readers); gx / gz central differences (one-sided
//          at the raster's edge, numpy's gradient), slope deg = atan |g|
//   class  cls nearest: cell floor((x - x0) / cell), clamped
//   R      the residual codes bilinear on the 64 m cell centres (clamped),
//          2^((code - 128) / RES_PER_STOP) per channel
//   albedo paint[cid] = a genome record key k: T x class_mean(k, h, slope)
//          x R x anchor (T = genometex genomeTexel at the texel: the world
//          seed, so neighbours meet); null: painterpal base (water 254: its
//          deep colour) linear x R (the bake predicted it / anchor) ->
//          sRGB 0..255 (surfacegen _srgb_enc)
//   light  lam = max(0, n.L) (groundref's sun: L from meta.lighting), water
//          flat (lam = sin alt, n_y 1: the frame painter's rule); sun / sky
//          visibility = the lit raster bilinear (each half clamped to
//          itself); E = groundref lightIrradiance(world, lam sun, sky, n_y)
//          -> sRGB-8 (light8) -> lit = sRGB8(lin(albedo) x lin(E8))
//          (texpaint.relight), as groundref paintRect stage 5.
import { lab, labLin, srgbToLin32, linToSrgb8, LUT8, lightIrradiance, RES_PER_STOP } from './groundref.js';
import { genomeTexParams, genomeTexel } from './genometex.js';
import { RING_CLASSES_FORMAT } from './ringtiers.js';
import { decodeNode, meshOf } from '../../groundm/codec.js';
import { nodeSide } from '../../groundm/lookup.js';

const f32 = Math.fround;
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
// surfacegen ANCHOR_DEFAULT (groundgen.js reads meta.tex.s2.anchor ?? it)
export const ANCHOR_DEFAULT = 2.96;
export const WATER_ID = 254;
const srgbDec = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
function srgbEnc(c) {                         // surfacegen _srgb_enc (float64, clipped)
  c = clamp(c, 0.0, 1.0);
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
}
// genome.class_mean: the record's mean_lin with its elev / slope Lab terms (groundref's, private there)
export function classMean(rec, L0, h, sdeg) {
  const e = rec.elev, s = rec.slope, de = (h - e.ref_m) / 100.0, ds = (sdeg - s.ref_deg) / 10.0;
  const c = labLin(L0[0] + de * e.dlab_per_100m[0] + ds * s.dlab_per_10deg[0],
    L0[1] + de * e.dlab_per_100m[1] + ds * s.dlab_per_10deg[1], L0[2] + de * e.dlab_per_100m[2] + ds * s.dlab_per_10deg[2]);
  return [Math.max(c[0], 1e-6), Math.max(c[1], 1e-6), Math.max(c[2], 1e-6)];
}

// ---------------------------------------------------------------- the world
export function ringWorld(meta, genome, pal, cj, gtTable) {
  const pano = meta.panorama, C = pano && pano.classes;
  if (!C || C.format !== RING_CLASSES_FORMAT) throw new Error(`ringref: meta.panorama.classes is not ${RING_CLASSES_FORMAT}`);
  const L = meta.tex && meta.tex.light, lt = meta.lighting;
  if (!(L && L.white > 0 && L.sky && L.ground && L.sun)) throw new Error('ringref: meta.tex.light (sky, ground, sun, white) missing');
  if (!(lt && Number.isFinite(lt.sun_alt_deg) && Number.isFinite(lt.sun_az_deg))) throw new Error('ringref: meta.lighting sun missing');
  const anchor = (meta.tex.s2 && meta.tex.s2.anchor) ?? ANCHOR_DEFAULT;
  if (!(C.residual && C.residual.anchor === anchor)) {
    throw new Error(`ringref: classes.residual.anchor ${C.residual && C.residual.anchor} is not the painter's anchor ${anchor}`);
  }
  if (C.residual.per_stop !== RES_PER_STOP || C.residual.offset !== 128) {
    throw new Error(`ringref: residual ${C.residual.enc} per_stop ${C.residual.per_stop} offset ${C.residual.offset} (want ${RES_PER_STOP} / 128)`);
  }
  if (!(genome && genome.classes)) throw new Error('ringref: genome.json has no classes');
  const keyOf = new Map(cj.classes.map((c) => [c.id, c.key]));
  keyOf.set(cj.water_id, 'water');
  // per class id: the genome record it paints, else its palette colour (linear)
  const recOf = new Map(), palLin = new Map();
  for (const [sid, k] of Object.entries(C.paint)) {
    const cid = Number(sid);
    if (k) {
      if (!genome.classes[k]) throw new Error(`ringref: classes.paint ${cid} -> ${k}: no such genome record`);
      recOf.set(cid, k);
      continue;
    }
    const pk = keyOf.get(cid), p = pal.palette[pk];
    const base = cid === cj.water_id ? p && p.deep : p && p.base;
    if (!base) throw new Error(`ringref: class ${cid} (${pk}) has neither a genome record nor a painterpal colour`);
    palLin.set(cid, base.map((v) => srgbDec(v / 255)));
  }
  const az = lt.sun_az_deg * Math.PI / 180, alt = lt.sun_alt_deg * Math.PI / 180;
  const fp = pano.frame_posts, ps = pano.post_m;
  return {
    genome, gtTable, pal, classes: C, pano, anchor, recOf, palLin, gP: new Map(), gLab: new Map(),
    light: { ...L, sunAltDeg: lt.sun_alt_deg, sunAzDeg: lt.sun_az_deg },
    sun: { sx: Math.sin(az) * Math.cos(alt), sy: Math.cos(az) * Math.cos(alt), sz: Math.sin(alt) },
    // the playable frame (game m) + 1 / (2 R_eff): the drop the ring mesh is curved by
    frame: [pano.origin_game_x + fp[0] * ps, pano.origin_game_z + fp[1] * ps, pano.origin_game_x + fp[2] * ps,
      pano.origin_game_z + fp[3] * ps],
    dropK: 0.5 / pano.earth_r_eff_m,
  };
}
export function ringRecord(world, key) {
  let P = world.gP.get(key);
  if (!P) {
    const rec = world.genome.classes[key];
    P = genomeTexParams(rec, key, world.gtTable);
    world.gP.set(key, P);
    world.gLab.set(key, lab(rec.mean_lin[0], rec.mean_lin[1], rec.mean_lin[2]));
  }
  return P;
}
// a record's Lab mean alone (ringPaint noTex: no genomeTexParams solve) -> world.gLab
function recLab(world, key) {
  const m = world.genome.classes[key].mean_lin, L = lab(m[0], m[1], m[2]);
  world.gLab.set(key, L);
  return L;
}
const ONE = [1, 1, 1];
// (R8c5) a record's genomeTexParams solved elsewhere (ringgen.js: the pool's genome.params) into
// the world's cache — what ringRecord would have stored: P in gP, the record's Lab mean in gLab
export function ringSetRecord(world, key, P) {
  const rec = world.genome.classes[key];
  world.gP.set(key, P);
  world.gLab.set(key, lab(rec.mean_lin[0], rec.mean_lin[1], rec.mean_lin[2]));
  return P;
}
// the earth drop at game (x, z): the old ring mesh was curved from the frame (bake _earth_drop);
// the painter no longer adds it (GM relief is flat-earth truth) — scripts/check-curve C1 reads it
export function ringDrop(world, x, z) {
  const F = world.frame, dx = Math.max(F[0] - x, 0, x - F[2]), dz = Math.max(F[1] - z, 0, z - F[3]);
  return (dx * dx + dz * dz) * world.dropK;
}

// ---------------------------------------------------------------- the pass's inputs
export function ringInputs(t, genomeUrl) { return [t.cls, t.lit, t.res, genomeUrl]; }

// a deterministic failure (gensched: remembered, never retried)
export function ringFault(msg) { const e = new Error(msg); e.permanent = true; return e; }
// EXACT-DATA-BEGIN (scripts/check-ring.mjs exact:no-canvas: no canvas, bitmap or readback in here)
// a raw u8 exact raster (ring cls / res): its n bytes as fetched, checked once against the bake's
// sha16 (the first 16 hex of the SHA-256 of the bytes) — a wrong size, a sha mismatch or no way to
// check it (no crypto.subtle) is a ringFault (deterministic)
const hex16 = (ab) => Array.from(new Uint8Array(ab, 0, 8), (b) => b.toString(16).padStart(2, '0')).join('');
export async function ringExact(ab, rec, n, what = 'ring raster') {
  const b = ab instanceof Uint8Array ? ab : new Uint8Array(ab);
  if (b.length !== n) throw ringFault(`${what}: ${b.length} B, want ${n} (raw u8)`);
  const S = globalThis.crypto && globalThis.crypto.subtle;
  if (!S) throw ringFault(`${what}: no crypto.subtle (not a secure context): its sha cannot be checked`);
  const h = hex16(await S.digest('SHA-256', b));
  if (!rec || h !== rec.sha) throw ringFault(`${what}: sha16 ${h}, the bake recorded ${rec && rec.sha}`);
  return b;
}
// EXACT-DATA-END
// the flat colour (sRGB8) of a sector given up (ring.fallback): the class histogram's mean palette
// colour (water: its deep colour) under the flat light (sky + sin(sun alt) x sun, / white)
export function ringFallbackRGB(meta, pal, cj) {
  const C = meta.panorama.classes, L = meta.tex.light, lt = meta.lighting;
  const keyOf = new Map(cj.classes.map((c) => [c.id, c.key]));
  keyOf.set(cj.water_id, 'water');
  const lin = [0, 0, 0];
  let n = 0;
  for (const [sid, cnt] of Object.entries((C.stats && C.stats.hist) || {})) {
    const cid = Number(sid), p = pal.palette[keyOf.get(cid)], base = p && (cid === cj.water_id ? p.deep : p.base);
    if (!base || !(cnt > 0)) continue;
    for (let c = 0; c < 3; c++) lin[c] += cnt * srgbDec(base[c] / 255);
    n += cnt;
  }
  if (!n) for (let c = 0; c < 3; c++) lin[c] = srgbDec(0.5);
  const lam = Math.max(0, Math.sin(lt.sun_alt_deg * Math.PI / 180));
  return lin.map((v, c) => Math.round(255 * srgbEnc((n ? v / n : v) * clamp((L.sky[c] + lam * L.sun[c]) / L.white, 0, 1))));
}

// ---------------------------------------------------------------- heights
// the relief nodes (GM03 bytes) -> chunks in game metres, lvl = the node's level
export function gmChunks(relief) {
  return relief.map((n) => {
    const m = meshOf(decodeNode(n.bytes)), s = nodeSide(n.k), ox = n.tx * s, oz = n.ty * s, nv = m.x.length;
    const x = new Float64Array(nv), z = new Float64Array(nv);
    for (let k = 0; k < nv; k++) { x[k] = ox + m.x[k]; z[k] = oz + m.z[k]; }
    return { x, y: m.y, z, idx: m.idx, lvl: n.k };
  });
}
// RNG1 chunks (u16 lattice i, quantized y, lattice j; u16 indices) -> game metres
export function decodeChunks(ab, chunks, pano) {
  const zs = pano.z_scale_cm / 100;
  return chunks.map((c) => {
    const v = new Uint16Array(ab, c.v, c.nv * 3), x = new Float64Array(c.nv), y = new Float64Array(c.nv), z = new Float64Array(c.nv);
    for (let k = 0; k < c.nv; k++) {
      x[k] = pano.origin_game_x + v[3 * k] * pano.post_m;
      y[k] = pano.z_base_m + v[3 * k + 1] * zs;
      z[k] = pano.origin_game_z + v[3 * k + 2] * pano.post_m;
    }
    return { x, y, z, idx: new Uint16Array(ab, c.i, c.ni) };
  });
}
export function rasterHeights(chunks, rect, w, h) {
  const tx = (rect[2] - rect[0]) / w, tz = (rect[3] - rect[1]) / h;
  const H = new Float32Array(w * h).fill(-Infinity), cov = new Uint8Array(w * h), lv = new Float64Array(w * h).fill(Infinity);
  for (const { x, y, z, idx, lvl = 0 } of chunks) {
    for (let t = 0; t + 2 < idx.length; t += 3) {
      const a = idx[t], b = idx[t + 1], c = idx[t + 2];
      const ax = x[a], az = z[a], ux = x[b] - ax, uz = z[b] - az, vx = x[c] - ax, vz = z[c] - az;
      const den = ux * vz - uz * vx;
      if (!(Math.abs(den) > 1e-12)) continue;
      const uy = y[b] - y[a], vy = y[c] - y[a];
      const i0 = Math.max(0, Math.ceil((Math.min(ax, ax + ux, ax + vx) - rect[0]) / tx - 0.5 - 1e-9));
      const i1 = Math.min(w - 1, Math.floor((Math.max(ax, ax + ux, ax + vx) - rect[0]) / tx - 0.5 + 1e-9));
      const k0 = Math.max(0, Math.ceil((Math.min(az, az + uz, az + vz) - rect[1]) / tz - 0.5 - 1e-9));
      const k1 = Math.min(h - 1, Math.floor((Math.max(az, az + uz, az + vz) - rect[1]) / tz - 0.5 + 1e-9));
      for (let k = k0; k <= k1; k++) {
        const pz = rect[1] + (k + 0.5) * tz - az;
        for (let i = i0; i <= i1; i++) {
          const px = rect[0] + (i + 0.5) * tx - ax, l1 = (px * vz - pz * vx) / den, l2 = (ux * pz - uz * px) / den;
          if (l1 < -1e-9 || l2 < -1e-9 || l1 + l2 > 1 + 1e-9) continue;
          const q = k * w + i, v = f32(y[a] + l1 * uy + l2 * vy);
          // the finest level wins, then the highest
          if (lvl < lv[q]) { lv[q] = lvl; H[q] = v; } else if (lvl === lv[q] && v > H[q]) H[q] = v;
          cov[q] = 1;
        }
      }
    }
  }
  // past every triangle: the nearest covered texel of the row (left first), then the column
  for (let k = 0; k < h; k++) {
    let a = -1, b = -1;
    for (let i = 0; i < w; i++) if (cov[k * w + i] === 1) { if (a < 0) a = i; b = i; }
    if (a < 0) continue;
    for (let i = 0; i < a; i++) { H[k * w + i] = H[k * w + a]; cov[k * w + i] = 2; }
    for (let i = a + 1; i < w; i++) if (!cov[k * w + i]) { H[k * w + i] = H[k * w + i - 1]; cov[k * w + i] = 2; }
  }
  let ka = -1, kb = -1;
  for (let k = 0; k < h; k++) if (cov[k * w]) { if (ka < 0) ka = k; kb = k; }
  if (ka < 0) throw new Error(`ringref: no ring triangle under [${rect}]`);
  for (let k = 0; k < h; k++) {
    if (cov[k * w]) continue;
    const s = k < ka ? ka : kb;
    for (let i = 0; i < w; i++) H[k * w + i] = H[s * w + i];
  }
  return H;
}

// the QUICK paint's heights (ringgen.js, before the relief nodes are in): w x h texel centres of rect
// (row 0 north) from at(x, z) = the ring core's sampler (curved: ring.js height, held inside its
// square by the caller) + ringDrop back = the flat-earth altitude ringPaint reads
export function quickHeights(at, world, rect, w, h) {
  const tx = (rect[2] - rect[0]) / w, tz = (rect[3] - rect[1]) / h, H = new Float32Array(w * h);
  for (let k = 0; k < h; k++) {
    const z = rect[1] + (k + 0.5) * tz;
    for (let i = 0; i < w; i++) {
      const x = rect[0] + (i + 0.5) * tx;
      H[k * w + i] = f32(at(x, z) + ringDrop(world, x, z));
    }
  }
  return H;
}

// ---------------------------------------------------------------- paint
// bilinear of a u8 grid (w x h, rows r0.., nc channels, channel ch) at cell-centre coordinates (u, v)
function bil8(G, w, h, nc, ch, u, v, r0 = 0) {
  const fu = Math.floor(u), fv = Math.floor(v), tu = clamp(u - fu, 0, 1), tv = clamp(v - fv, 0, 1);
  const i0 = clamp(fu, 0, w - 1), i1 = clamp(fu + 1, 0, w - 1), j0 = clamp(fv, 0, h - 1) + r0, j1 = clamp(fv + 1, 0, h - 1) + r0;
  const a = G[(j0 * w + i0) * nc + ch], b = G[(j0 * w + i1) * nc + ch], c = G[(j1 * w + i0) * nc + ch], d = G[(j1 * w + i1) * nc + ch];
  return (a * (1 - tu) + b * tu) * (1 - tv) + (c * (1 - tu) + d * tu) * tv;
}
export function ringPaint(world, sector, level, heights, opts = {}) {
  const C = world.classes, cell = C.cell_m[String(sector.tier)], [pw, ph] = sector.px, rect = sector.rect;
  const w = pw >> level, h = ph >> level, tx = cell * pw / w, tz = cell * ph / h, N = w * h;
  if (heights.length !== N) throw new Error(`ringref: heights ${heights.length}, want ${w} x ${h}`);
  if (sector.cls.length !== pw * ph || (!opts.noVis && sector.lit.length !== 2 * pw * ph)) throw new Error(`ringref: ${sector.id} inputs are not ${pw} x ${ph}`);
  const xs = new Float64Array(w), zs = new Float64Array(h), sd = opts.seed || 0;
  for (let i = 0; i < w; i++) xs[i] = rect[0] + (i + 0.5) * tx;
  for (let k = 0; k < h; k++) zs[k] = rect[1] + (k + 0.5) * tz;
  // the relief's heights as they stand (flat-earth truth: no earth drop re-added)
  const hT = Float32Array.from(heights);
  const R = sector.res, [rw, rh] = R.px, rc = C.residual.cell_m;
  const albedo = new Float32Array(N * 3), lin = new Float32Array(N * 3), E = new Uint8Array(N * 3), lit = new Uint8Array(N * 3);
  const rgba = new Uint8Array(N * 4), sun = world.sun;
  for (let k = 0; k < h; k++) {
    for (let i = 0; i < w; i++) {
      const q = k * w + i, x = xs[i], z = zs[k];
      // the normal: central differences of the true altitude (one-sided at the edge)
      const il = Math.max(i - 1, 0), ir = Math.min(i + 1, w - 1), ku = Math.max(k - 1, 0), kd = Math.min(k + 1, h - 1);
      const gx = (hT[k * w + ir] - hT[k * w + il]) / ((ir - il) * tx), gz = (hT[kd * w + i] - hT[ku * w + i]) / ((kd - ku) * tz);
      const g2 = gx * gx + gz * gz, sdeg = Math.atan(Math.sqrt(g2)) * (180 / Math.PI);
      const cid = sector.cls[clamp(Math.floor((z - rect[1]) / cell), 0, ph - 1) * pw + clamp(Math.floor((x - rect[0]) / cell), 0, pw - 1)];
      // the residual
      const Rm = [1, 1, 1];
      if (!opts.noResidual) {
        const u = (x - R.rect[0]) / rc - 0.5, v = (z - R.rect[1]) / rc - 0.5;
        for (let c = 0; c < 3; c++) Rm[c] = 2 ** ((bil8(R.codes, rw, rh, 3, c, u, v) - 128.0) / RES_PER_STOP);
      }
      const key = world.recOf.get(cid), col = [0, 0, 0];
      if (key) {
        const T = opts.noTex ? ONE : genomeTexel(ringRecord(world, key), x + sd, z + sd, tx);
        const base = classMean(world.genome.classes[key], world.gLab.get(key) || recLab(world, key), hT[q], sdeg);
        for (let c = 0; c < 3; c++) col[c] = T[c] * base[c] * Rm[c] * world.anchor;
      } else {
        const p = world.palLin.get(cid);
        if (!p) throw new Error(`ringref: ${sector.id} class byte ${cid} is not in classes.paint`);
        for (let c = 0; c < 3; c++) col[c] = p[c] * Rm[c];
      }
      for (let c = 0; c < 3; c++) { lin[3 * q + c] = col[c]; albedo[3 * q + c] = f32(srgbEnc(col[c]) * 255.0); }
      // the light
      let lam, ny;
      if (cid === WATER_ID) { lam = f32(sun.sz); ny = 1; } else {
        const n = f32(Math.sqrt(f32(f32(f32(gx * gx) + f32(gz * gz)) + 1)));
        lam = clamp(f32(f32(f32(f32(f32(gx) * f32(-sun.sx)) + f32(f32(gz) * f32(sun.sy))) + f32(sun.sz)) / n), 0, 1);
        ny = f32(1 / n);
      }
      const u = (x - rect[0]) / cell - 0.5, v = (z - rect[1]) / cell - 0.5;
      const vis = opts.noVis ? 1 : f32(bil8(sector.lit, pw, ph, 1, 0, u, v) / 255);
      const sky = opts.noVis ? 1 : f32(bil8(sector.lit, pw, ph, 1, 0, u, v, ph) / 255);
      const e = lightIrradiance(world, f32(lam * vis), sky, ny);
      for (let c = 0; c < 3; c++) {
        const e8 = linToSrgb8(e[c]);
        E[3 * q + c] = e8;
        lit[3 * q + c] = linToSrgb8(f32(srgbToLin32(f32(albedo[3 * q + c] / f32(255))) * LUT8[e8]));
      }
      const d = (h - 1 - k) * w + i;
      rgba[4 * d] = lit[3 * q]; rgba[4 * d + 1] = lit[3 * q + 1]; rgba[4 * d + 2] = lit[3 * q + 2]; rgba[4 * d + 3] = 255;
    }
  }
  return { rgba, albedo, lin, E, lit, h: hT, w, h_px: h, tx, tz };
}
